import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { useState } from "react";

import { ApiError } from "@/api/client";
import { Button, Dialog, SelectField, TextField } from "@/components/ui";

import { ProductImageField } from "./ProductImageField";
import { formatMoney } from "@/lib/format";
import { catalogueRepository, type StockProduct } from "@/repositories/catalogueRepository";
import { useAuthStore } from "@/stores/authStore";

import { useCategories } from "./useCatalogueData";

/**
 * Editing a product.
 *
 * Two halves, deliberately separated, because they are two different acts.
 *
 * *Details* — name, barcodes, category, VAT, nicknames — is an ordinary edit and
 * goes through `PATCH /products/{id}/`.
 *
 * *Price* is not. Changing what a customer pays needs a reason, an author and a
 * history row, and is gated by its own permission — so the price fields sit in
 * their own section, ask for a reason, and go through `/products/{id}/price/`.
 * Folding them into the same request would let a price change through disguised
 * as a spelling correction, which is precisely what the append-only price table
 * exists to prevent.
 */
export function ProductForm({ product, onClose }: { product: StockProduct; onClose: () => void }) {
  const client = useQueryClient();
  const can = useAuthStore((s) => s.can);
  const canChangePrice = can("products.change_selling_price");
  const canSeeCost = can("products.view_cost_price");

  const categories = useCategories();
  const taxes = useQuery({
    queryKey: ["catalogue", "tax-categories"],
    queryFn: catalogueRepository.taxCategories,
    staleTime: 5 * 60_000,
  });
  const units = useQuery({
    queryKey: ["catalogue", "units"],
    queryFn: catalogueRepository.units,
    staleTime: Infinity,
  });

  const [name, setName] = useState(product.name);
  const [brand, setBrand] = useState(product.brand);
  const [sku, setSku] = useState(product.sku ?? "");
  const [packSize, setPackSize] = useState(product.pack_size);
  const [keywords, setKeywords] = useState(product.search_keywords ?? "");
  const [shelf, setShelf] = useState(product.shelf_location);
  const [categoryId, setCategoryId] = useState(product.category ?? "");
  const [taxId, setTaxId] = useState(product.tax?.id ?? "");
  const [unitCode, setUnitCode] = useState(product.unit?.code ?? "ea");
  const [reorder, setReorder] = useState(String(Number(product.reorder_level)));
  const [barcodes, setBarcodes] = useState<string[]>(
    product.barcodes.map((entry) => entry.barcode),
  );
  const [newBarcode, setNewBarcode] = useState("");

  const [price, setPrice] = useState(product.selling_price);
  const [priceReason, setPriceReason] = useState("");

  const priceChanged = canChangePrice && price !== product.selling_price;

  const save = useMutation({
    mutationFn: async () => {
      await catalogueRepository.updateProduct(product.id, {
        name,
        brand,
        sku: sku.trim() === "" ? null : sku,
        pack_size: packSize,
        search_keywords: keywords,
        shelf_location: shelf,
        category_id: categoryId || null,
        ...(taxId ? { tax_category_id: taxId } : {}),
        unit_code: unitCode,
        reorder_level: reorder || "0",
        barcodes,
        // The version this form was opened with. If somebody else has saved in
        // the meantime the server refuses with 409 rather than overwriting them.
        expected_version: product.version,
      });

      if (priceChanged) {
        await catalogueRepository.changePrice(product.id, {
          amount: price,
          reason: priceReason,
        });
      }
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["inventory"] });
      void client.invalidateQueries({ queryKey: ["catalogue"] });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Edit product"
      description={product.name}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={name.trim() === "" || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <TextField label="Product name" value={name} onChange={(e) => setName(e.target.value)} />

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label="Brand" value={brand} onChange={(e) => setBrand(e.target.value)} />
          <TextField
            label="Pack size"
            hint="As printed: 500ml, 2kg."
            value={packSize}
            onChange={(e) => setPackSize(e.target.value)}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="Category"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            options={[
              { value: "", label: "No category" },
              ...(categories.data ?? []).map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
          <SelectField
            label="VAT"
            hint="Bread and maize meal are zero rated; cold drinks are not."
            value={taxId}
            onChange={(e) => setTaxId(e.target.value)}
            options={(taxes.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="Sold by"
            value={unitCode}
            onChange={(e) => setUnitCode(e.target.value)}
            options={(units.data ?? []).map((u) => ({ value: u.code, label: u.name }))}
          />
          <TextField
            label="Reorder level"
            hint="Warn me when stock falls to this."
            inputMode="decimal"
            value={reorder}
            onChange={(e) => setReorder(e.target.value)}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Your code (SKU)"
            hint="Optional."
            value={sku}
            onChange={(e) => setSku(e.target.value)}
          />
          <TextField
            label="Shelf"
            hint="Free text — where it lives in the shop."
            value={shelf}
            onChange={(e) => setShelf(e.target.value)}
          />
        </div>

        <TextField
          label="Also known as"
          hint="Comma-separated nicknames, so “coke” finds Coca-Cola. Your cashiers search by these."
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
        />

        <BarcodeEditor
          barcodes={barcodes}
          pending={newBarcode}
          onPending={setNewBarcode}
          onAdd={() => {
            const code = newBarcode.trim();
            if (code && !barcodes.includes(code)) setBarcodes([...barcodes, code]);
            setNewBarcode("");
          }}
          onRemove={(code) => setBarcodes(barcodes.filter((entry) => entry !== code))}
        />

        <ProductImageField product={product} />

        {/* ── Price, in its own section ─────────────────────────────────────── */}
        {canChangePrice && (
          <section className="rounded-card border border-line p-4">
            <h3 className="text-sm font-semibold text-ink-900">Selling price</h3>
            <p className="mt-0.5 text-xs text-ink-500">
              Changing this is recorded separately, with your name and the reason — it is what a
              price-change report reads back.
            </p>

            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <TextField
                label="Price"
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
              {canSeeCost && product.cost_price && (
                <div className="self-end pb-2 text-sm text-ink-500">
                  Costs {formatMoney(product.cost_price)}
                  {Number(price) > 0 && Number(product.cost_price) > 0 && (
                    <span className="ml-1">
                      · margin{" "}
                      {(
                        ((Number(price) - Number(product.cost_price)) / Number(price)) *
                        100
                      ).toFixed(0)}
                      %
                    </span>
                  )}
                </div>
              )}
            </div>

            {priceChanged && (
              <TextField
                label="Why is the price changing?"
                hint="Shown in the price history. “Supplier increase”, “promotion ended”."
                className="mt-3"
                value={priceReason}
                onChange={(e) => setPriceReason(e.target.value)}
              />
            )}
          </section>
        )}

        {save.isError && (
          <p className="rounded-card border border-danger-500/30 bg-danger-50 px-4 py-3 text-sm text-danger-700">
            {save.error instanceof ApiError && save.error.status === 409
              ? "Somebody else changed this product while you had it open. Close this and open it again so you do not overwrite their edit."
              : save.error instanceof ApiError
                ? save.error.detail
                : "That could not be saved. Please try again."}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function BarcodeEditor({
  barcodes,
  pending,
  onPending,
  onAdd,
  onRemove,
}: {
  barcodes: string[];
  pending: string;
  onPending: (value: string) => void;
  onAdd: () => void;
  onRemove: (code: string) => void;
}) {
  return (
    <div>
      <p className="text-sm font-medium text-ink-900">Barcodes</p>
      <p className="mt-0.5 text-xs text-ink-500">
        A product may carry several — a single can and a six-pack scan differently.
      </p>

      {barcodes.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-2">
          {barcodes.map((code) => (
            <li
              key={code}
              className="inline-flex items-center gap-1.5 rounded-pill border border-line bg-canvas py-1 pl-3 pr-1.5 font-mono text-xs"
            >
              {code}
              <button
                type="button"
                onClick={() => onRemove(code)}
                aria-label={`Remove barcode ${code}`}
                className="grid size-5 place-items-center rounded-full text-ink-500 hover:bg-line hover:text-ink-900"
              >
                <X className="size-3" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-2 flex items-end gap-2">
        <TextField
          label="Add a barcode"
          inputMode="numeric"
          className="flex-1"
          value={pending}
          onChange={(e) => onPending(e.target.value)}
          onKeyDown={(event) => {
            // A scanner types the digits then Enter. Submitting the whole form
            // on that would be the wrong thing entirely.
            if (event.key === "Enter") {
              event.preventDefault();
              onAdd();
            }
          }}
        />
        <Button
          variant="secondary"
          className="mb-0.5"
          icon={<Plus className="size-4" aria-hidden />}
          onClick={onAdd}
          disabled={pending.trim() === ""}
        >
          Add
        </Button>
      </div>
    </div>
  );
}
